import React, { useContext, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import WarningIcon from "@mui/icons-material/Warning";
import { UserContext } from "../context/UserProvider";
import useApiEp from "../hooks/useApiEp";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedIcon from "@mui/icons-material/Verified";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChatIcon from "@mui/icons-material/Chat";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FacebookIcon from "@mui/icons-material/Facebook";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import StatsCards from "./DashStats";
import ListDataItems from "./ListDataItems";
import MessageVolumeCard from "./MessageVolumeCard";
import WeeklyCreditsCard from "./WeeklyCreditsCard";
import { useAccount } from "../context/AccountContext";

const pieColors = [
  "#4f46e5",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];
export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getPhoneDetailsMessage = (error) => {
  const rawMessage = String(
    error?.response?.data?.error?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      ""
  ).toLowerCase();

  const needsReconnect = [
    "unsupported get request",
    "missing permissions",
    "does not exist",
    "graph api",
  ].some((phrase) => rawMessage.includes(phrase));

  if (needsReconnect) {
    return {
      title: "WhatsApp connection needs attention",
      description:
        "We could not sync this number's WhatsApp profile. It may have been disconnected or may need its permissions refreshed. Your dashboard and campaign data are still available.",
    };
  }

  return {
    title: "WhatsApp details are taking a little longer",
    description:
      "We could not refresh the WhatsApp profile right now. The rest of your dashboard is available, and you can try syncing again in a moment.",
  };
};

function PhoneDetailsState({ error, onRetry }) {
  const copy = error
    ? getPhoneDetailsMessage(error)
    : {
        title: "Connect a WhatsApp number",
        description:
          "Link your WhatsApp Business number to see its connection status, quality rating, and messaging limit here.",
      };

  return (
    <Box
      sx={{
        border: "1px solid #dbe5f0",
        bgcolor: "#f8fbff",
        borderRadius: 2.5,
        p: 2.25,
        mt: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: "#e8f1fb",
            color: "#2563a7",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <CloudSyncRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
            {copy.title}
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, lineHeight: 1.55, color: "#64748b", mt: 0.5 }}
          >
            {copy.description}
          </Typography>
          {error && (
            <Button
              size="small"
              variant="text"
              startIcon={<SyncRoundedIcon />}
              onClick={onRetry}
              sx={{
                mt: 1,
                ml: -1,
                color: "#2563a7",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Retry sync
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function WabaDashboard() {
  const { selectedAccount } = useAccount();
  const { user } = useContext(UserContext);
  const { data: AisensyBalance } = useApiEp(
    `/user/get-aisensy-partner-details`,
    user?.userType === "Root" || user?.userType === "SuperAdmin"
  );
  const { data: TemplateStats } = useApiEp(`/api/get-template-category-stats`);
  const {
    data: PhoneData,
    isLoading: isPhoneLoading,
    isError: isPhoneError,
    error: phoneError,
    refetch: refetchPhone,
  } = useApiEp(`/user/phone-details?cid=${selectedAccount}`);

  // Modern light color palette with vibrant accents
  const colors = {
    primary: "#4f46e5",
    secondary: "#7c3aed",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#06b6d4",
    background: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#1e293b",
    textSecondary: "#64748b",
    accent: "#8b5cf6",
  };

  const templateUsage = useMemo(() => {
    if (!TemplateStats) {
      return [];
    } else {
      return TemplateStats.map((item) => ({
        name: item.category,
        value: item.count,
      }));
    }
  }, [TemplateStats]);

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, md: 3.5 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        background:
          "linear-gradient(180deg, #f7f9fd 0%, #f4f7fb 48%, #f8fafc 100%)",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.2,
              fontWeight: 750,
              letterSpacing: "-0.025em",
              color: colors.textPrimary,
            }}
          >
            {user ? `Welcome back, ${user.customerId}` : "Growth overview"}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: colors.textSecondary, mt: 0.5 }}>
            Monitor campaign activity, channel balances, and audience engagement.
          </Typography>
        </Box>
        <Chip
          icon={<AutoGraphRoundedIcon />}
          label="Growth overview"
          sx={{
            bgcolor: "#eef2ff",
            color: "#4f46e5",
            border: "1px solid #dfe3ff",
            fontWeight: 700,
            "& .MuiChip-icon": { color: "#4f46e5" },
          }}
        />
      </Box>

      {/* Balance & Stats Cards */}
      <Grid container spacing={3}>
        {/* Balance Card */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#fff",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
              border: "1px solid #e5eaf1",
              height: "100%",
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <Box sx={{ mb: 2.25 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                  Campaign activity
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.35 }}>
                  Your latest WhatsApp campaign totals and audience reach
                </Typography>
              </Box>

              <ListDataItems />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                {user?.userType === "SuperAdmin" && (
                  <Chip
                    label="Super Admin Mode"
                    sx={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#fff",
                      fontWeight: 600,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.05)" },
                        "100%": { transform: "scale(1)" },
                      },
                    }}
                  />
                )}

                {user?.userType === "Root" ||
                user?.userType === "SuperAdmin" ? (
                  <>
                    {AisensyBalance && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            fontWeight: 500,
                            fontSize: "0.9rem",
                          }}
                        >
                          Balance:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: colors.primary,
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          ₹{AisensyBalance.centralBalance.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        fontWeight: 500,
                        fontSize: "0.85rem",
                      }}
                    >
                      Your plan expiring on:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.error, // or colors.primary if you prefer
                        fontWeight: 600,
                      }}
                    >
                      {user?.expiryDate
                        ? new Date(user.expiryDate).toLocaleDateString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "NA"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: "#fff",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
              border: "1px solid #e5eaf1",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                pb: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WhatsAppIcon sx={{ color: "#16a36a", fontSize: 22 }} />
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                    WhatsApp account
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.textSecondary }}>
                    Connection and number health
                  </Typography>
                </Box>
              </Box>
              {PhoneData?.data?.status === "CONNECTED" && (
                <Chip
                  size="small"
                  label="Connected"
                  sx={{ bgcolor: "#e8f8f0", color: "#087a55", fontWeight: 700 }}
                />
              )}
            </Box>
            {PhoneData ? (
              <Stack
                spacing={0}
                divider={
                  <Divider
                    sx={{
                      borderColor: "rgba(99, 102, 241, 0.08)",
                    }}
                  />
                }
              >
                {/* Account Status */}
                <Box sx={{ p: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          PhoneData.data?.status === "CONNECTED"
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {PhoneData.data?.status === "CONNECTED" ? (
                        <LinkIcon
                          sx={{
                            color: colors.success,
                            fontSize: "1.1rem",
                          }}
                        />
                      ) : (
                        <LinkOffIcon
                          sx={{
                            color: colors.error,
                            fontSize: "1.1rem",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Account Status
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.status || "Unknown"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Phone Number */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PhoneIcon
                        sx={{
                          color: colors.primary,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.display_phone_number}
                        {PhoneData.data?.is_official_business_account && (
                          <VerifiedIcon
                            sx={{
                              color: colors.success,
                              fontSize: "1rem",
                              ml: 0.5,
                              verticalAlign: "text-bottom",
                            }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Verified Name */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <BadgeIcon
                        sx={{
                          color: colors.warning,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Verified Name
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.verified_name || "Not verified"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Quality Rating */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          PhoneData.data?.quality_score?.score === "GREEN"
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {PhoneData.data?.quality_score?.score === "GREEN" ||
                      PhoneData.data?.quality_rating === "GREEN" ? (
                        <CheckCircleIcon
                          sx={{
                            color: colors.success,
                            fontSize: "1.1rem",
                          }}
                        />
                      ) : (
                        <WarningIcon
                          sx={{
                            color: colors.warning,
                            fontSize: "1.1rem",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Quality Rating
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.quality_score?.score ||
                          PhoneData.data?.quality_rating ||
                          "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Messaging Limit */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(8, 145, 178, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ChatIcon
                        sx={{
                          color: colors.info,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Messaging Limit
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.whatsapp_business_manager_messaging_limit?.replace(
                          "TIER_",
                          ""
                        ) || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            ) : isPhoneLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                <CircularProgress
                  size={28}
                  sx={{
                    color: colors.primary,
                    "& .MuiCircularProgress-circle": {
                      strokeWidth: 3,
                    },
                  }}
                />
              </Box>
            ) : isPhoneError ? (
              <PhoneDetailsState error={phoneError} onRetry={refetchPhone} />
            ) : (
              <PhoneDetailsState onRetry={refetchPhone} />
            )}
            {!user?.partner &&
              !isPhoneLoading &&
              PhoneData?.data?.status !== "CONNECTED" && (
                <Stack direction={"column"} spacing={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<FacebookIcon />}
                    onClick={() => {
                      localStorage.setItem("cusId", user.customerId);
                      localStorage.setItem("partner", "haptik");
                      window.launchWhatsAppSignup("1108770184427834");
                    }}
                    sx={{
                      mt: 3,
                      background:
                        "linear-gradient(135deg, #1877f2 0%, #165fe5 100%)",
                      color: "white",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(24, 119, 242, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #165fe5 0%, #144fd1 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(24, 119, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Connect with Haptik
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<FacebookIcon />}
                    onClick={() => {
                      localStorage.setItem("cusId", user.customerId);
                      localStorage.setItem("partner", "aisensy");
                      window.launchWhatsAppSignup("1240838350783888");
                    }}
                    sx={{
                      mt: 3,
                      background:
                        "linear-gradient(135deg, #1877f2 0%, #165fe5 100%)",
                      color: "white",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(24, 119, 242, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #165fe5 0%, #144fd1 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(24, 119, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Connect with AiSensy
                  </Button>
                </Stack>
              )}
          </Card>
        </Grid>
      </Grid>
      <StatsCards />

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Monthly Messages Chart */}
        <Grid item xs={12} lg={7}>
          <MessageVolumeCard
            title="Monthly Message Volume"
            chipLabel="2025"
            colors={{
              primary: "#4f46e5",
              secondary: "#6366f1",
              textPrimary: "#0f172a",
              textSecondary: "#64748b",
            }}
          />
        </Grid>

        {/* Template Usage Chart */}
        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#fff",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
              border: "1px solid #e5eaf1",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: colors.textPrimary,
                mb: 3,
                "&::after": {
                  content: '""',
                  display: "block",
                  width: "48px",
                  height: "3px",
                  background: `linear-gradient(90deg, ${colors.secondary}, transparent)`,
                  borderRadius: "3px",
                  mt: 1,
                },
              }}
            >
              Template Category Distribution
            </Typography>

            <Box sx={{ position: "relative", height: "260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={templateUsage}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={90}
                    paddingAngle={2}
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {templateUsage.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={pieColors[index % pieColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "rgba(255, 255, 255, 0.95)",
                      border: `1px solid ${colors.primary}30`,
                      borderRadius: "12px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                      color: colors.textPrimary,
                      backdropFilter: "blur(20px)",
                    }}
                    itemStyle={{
                      color: colors.textPrimary,
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                    labelStyle={{
                      color: colors.primary,
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color={colors.textSecondary}>
                  Total Usage
                </Typography>
                <Typography
                  variant="h4"
                  color={colors.primary}
                  fontWeight={700}
                >
                  {templateUsage.reduce((sum, item) => sum + item.value, 0)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
                  templates
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 1.5,
                mt: 3,
              }}
            >
              {templateUsage.map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      background: pieColors[index % pieColors.length],
                      mr: 1,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Typography variant="body2" color={colors.textSecondary}>
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Credits Usage Chart */}
      <WeeklyCreditsCard colors={colors} />
    </Box>
  );
}
